import React, { useState, useEffect } from 'react';
import { usuariosAPI } from '../services/api';

function UsuarioForm({ usuario, onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    nombre: '',
    apellidos: '',
    email: '',
    nombre_empresa: '',
    nif_cif: '',
    direccion: '',
    codigo_postal: '',
    ciudad: '',
    provincia: '',
    telefono: '',
    rol: 'coach',
    tipo_usuario: 'coach',
    activo: 1,
    correo_personalizado: 0,
    smtp_host: '',
    smtp_port: '587',
    smtp_user: '',
    smtp_pass: '',
    smtp_secure: 'tls',
    smtp_from_email: '',
    smtp_from_name: '',
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [faviconPreview, setFaviconPreview] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);

  useEffect(() => {
    if (usuario) {
      setFormData({
        username: usuario.username || '',
        password: '', // No mostrar contraseña existente
        nombre: usuario.nombre || '',
        apellidos: usuario.apellidos || '',
        email: usuario.email || '',
        nombre_empresa: usuario.nombre_empresa || '',
        nif_cif: usuario.nif_cif || '',
        direccion: usuario.direccion || '',
        codigo_postal: usuario.codigo_postal || '',
        ciudad: usuario.ciudad || '',
        provincia: usuario.provincia || '',
        telefono: usuario.telefono || '',
        rol: usuario.rol || 'coach',
        tipo_usuario: usuario.tipo_usuario || 'coach',
        activo: usuario.activo !== undefined ? usuario.activo : 1,
        correo_personalizado: usuario.correo_personalizado === true || usuario.correo_personalizado === 1 ? 1 : 0,
        smtp_host: usuario.smtp_host || '',
        smtp_port: usuario.smtp_port != null ? String(usuario.smtp_port) : '587',
        smtp_user: usuario.smtp_user || '',
        smtp_pass: '', // No cargar contraseña; dejar vacío para no cambiar
        smtp_secure: usuario.smtp_secure || 'tls',
        smtp_from_email: usuario.smtp_from_email || '',
        smtp_from_name: usuario.smtp_from_name || '',
      });
      // Cargar preview del logo si existe
      if (usuario.logo) {
        setLogoPreview(usuario.logo);
      }
      // Cargar preview del favicon si existe
      if (usuario.favicon) {
        setFaviconPreview(usuario.favicon);
      }
    }
  }, [usuario]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo de archivo
      if (!file.type.match('image.*')) {
        alert('Por favor, selecciona un archivo de imagen');
        return;
      }
      
      // Validar tamaño (máximo 2MB)
      if (file.size > 2 * 1024 * 1024) {
        alert('El archivo es demasiado grande. Máximo 2MB');
        return;
      }
      
      setLogoFile(file);
      
      // Crear preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFaviconChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo de archivo (favicon puede ser ICO, PNG, SVG, etc.)
      const allowedTypes = ['image/x-icon', 'image/vnd.microsoft.icon', 'image/png', 'image/jpeg', 'image/gif', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type) && !file.name.toLowerCase().endsWith('.ico')) {
        alert('Por favor, selecciona un archivo de favicon válido (ICO, PNG, SVG, etc.)');
        return;
      }
      
      // Validar tamaño (máximo 1MB para favicon)
      if (file.size > 1 * 1024 * 1024) {
        alert('El archivo es demasiado grande. Máximo 1MB');
        return;
      }
      
      setFaviconFile(file);
      
      // Crear preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFaviconPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    const username = (formData.username || '').trim();
    const nombre = (formData.nombre || '').trim();
    const apellidos = (formData.apellidos || '').trim();
    if (!username) {
      alert('El nombre de usuario es obligatorio.');
      return;
    }
    if (!nombre) {
      alert('El nombre es obligatorio.');
      return;
    }
    if (!apellidos) {
      alert('Los apellidos son obligatorios.');
      return;
    }
    if (!usuario && !formData.password) {
      alert('La contraseña es obligatoria para nuevos usuarios.');
      return;
    }

    const data = { ...formData, username, nombre, apellidos };
    
    // Si es edición y no se proporciona password, no incluir el campo
    if (usuario && !formData.password) {
      delete data.password;
    }
    
    // Si hay un logo nuevo, subirlo primero
    if (logoFile && usuario) {
      try {
        setUploadingLogo(true);
        const response = await usuariosAPI.uploadLogo(usuario.id, logoFile);
        if (response.data.success) {
          data.logo = response.data.logo;
        }
      } catch (error) {
        alert('No se pudo subir el logo. ' + (error.response?.data?.error || error.message));
        setUploadingLogo(false);
        return;
      } finally {
        setUploadingLogo(false);
      }
    }
    
    // Si hay un favicon nuevo, subirlo primero
    if (faviconFile && usuario) {
      try {
        setUploadingFavicon(true);
        const response = await usuariosAPI.uploadFavicon(usuario.id, faviconFile);
        if (response.data.success) {
          data.favicon = response.data.favicon;
        }
      } catch (error) {
        alert('No se pudo subir el favicon. ' + (error.response?.data?.error || error.message));
        setUploadingFavicon(false);
        return;
      } finally {
        setUploadingFavicon(false);
      }
    }
    
    onSubmit(data);
  };

  return (
    <div
      className="modal"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px',
        overflowY: 'auto',
        zIndex: 4000,
      }}
    >
      <div className="modal-content" style={{ position: 'relative', zIndex: 4001 }}>
        <div className="modal-header">
          <h2>{usuario ? 'Editar Usuario' : 'Nuevo Usuario'}</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Username *</label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              disabled={!!usuario} // No permitir cambiar username al editar
            />
            {usuario && (
              <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                El username no se puede cambiar
              </small>
            )}
          </div>
          
          <div className="form-group">
            <label>
              {usuario ? 'Nueva Contraseña (dejar vacío para no cambiar)' : 'Contraseña *'}
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required={!usuario}
            />
          </div>
          
          <div className="form-group">
            <label>Nombre *</label>
            <input
              type="text"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label>Apellidos *</label>
            <input
              type="text"
              name="apellidos"
              value={formData.apellidos}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
            />
          </div>

          <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #eee' }}>
            <strong style={{ display: 'block', marginBottom: '12px', color: '#555' }}>Datos fiscales (se usan en las facturas que creas como admin)</strong>
            <div className="form-group">
              <label>Nombre de empresa</label>
              <input
                type="text"
                name="nombre_empresa"
                value={formData.nombre_empresa}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>NIF/CIF</label>
              <input
                type="text"
                name="nif_cif"
                value={formData.nif_cif}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Dirección</label>
              <input
                type="text"
                name="direccion"
                value={formData.direccion}
                onChange={handleChange}
              />
            </div>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ flex: '1 1 120px' }}>
                <label>Código postal</label>
                <input
                  type="text"
                  name="codigo_postal"
                  value={formData.codigo_postal}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group" style={{ flex: '2 1 180px' }}>
                <label>Ciudad</label>
                <input
                  type="text"
                  name="ciudad"
                  value={formData.ciudad}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group" style={{ flex: '2 1 180px' }}>
                <label>Provincia</label>
                <input
                  type="text"
                  name="provincia"
                  value={formData.provincia}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Teléfono</label>
              <input
                type="tel"
                name="telefono"
                value={formData.telefono}
                onChange={handleChange}
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>Rol *</label>
            <select
              name="rol"
              value={formData.rol}
              onChange={handleChange}
              required
            >
              <option value="coach">Usuario</option>
              <option value="admin">Administrador</option>
            </select>
          </div>

          {formData.rol !== 'admin' && (
            <div className="form-group">
              <label>Tipo de usuario *</label>
              <select
                name="tipo_usuario"
                value={formData.tipo_usuario}
                onChange={handleChange}
                required
              >
                <option value="coach">Coach</option>
                <option value="comercial">Comercial</option>
              </select>
              <small style={{ display: 'block', color: '#666', fontSize: '12px', marginTop: '4px' }}>
                {formData.tipo_usuario === 'coach'
                  ? 'El usuario verá "Sesiones" en su menú y funcionalidades orientadas a coaching.'
                  : 'El usuario verá "Reuniones" en su menú y funcionalidades orientadas a comercial.'}
              </small>
            </div>
          )}

          {formData.rol === 'coach' && (
            <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #eee' }}>
              <strong style={{ display: 'block', marginBottom: '12px', color: '#555' }}>Correo personalizado</strong>
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                Si está activado, este coach usará su propio servidor SMTP para enviar emails (facturas, presupuestos, recordatorios de cita). Si está desactivado, se usa el servidor genérico de config.php.
              </p>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    name="correo_personalizado"
                    checked={formData.correo_personalizado === 1}
                    onChange={handleChange}
                  />
                  {' '}Usar servidor de correo personalizado
                </label>
              </div>
              {formData.correo_personalizado === 1 && (
                <>
                  <div className="form-group">
                    <label>SMTP Host</label>
                    <input
                      type="text"
                      name="smtp_host"
                      value={formData.smtp_host}
                      onChange={handleChange}
                      placeholder="smtp.ejemplo.com"
                    />
                  </div>
                  <div className="form-group">
                    <label>SMTP Puerto</label>
                    <input
                      type="text"
                      name="smtp_port"
                      value={formData.smtp_port}
                      onChange={handleChange}
                      placeholder="587"
                    />
                  </div>
                  <div className="form-group">
                    <label>SMTP Usuario</label>
                    <input
                      type="text"
                      name="smtp_user"
                      value={formData.smtp_user}
                      onChange={handleChange}
                      placeholder="usuario@ejemplo.com"
                    />
                  </div>
                  <div className="form-group">
                    <label>SMTP Contraseña</label>
                    <input
                      type="password"
                      name="smtp_pass"
                      value={formData.smtp_pass}
                      onChange={handleChange}
                      placeholder="Dejar vacío para no cambiar"
                    />
                  </div>
                  <div className="form-group">
                    <label>SMTP Seguro</label>
                    <select
                      name="smtp_secure"
                      value={formData.smtp_secure}
                      onChange={handleChange}
                    >
                      <option value="tls">TLS</option>
                      <option value="ssl">SSL</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Email remitente (From)</label>
                    <input
                      type="text"
                      name="smtp_from_email"
                      value={formData.smtp_from_email}
                      onChange={handleChange}
                      placeholder="noreply@ejemplo.com"
                    />
                  </div>
                  <div className="form-group">
                    <label>Nombre remitente (From Name)</label>
                    <input
                      type="text"
                      name="smtp_from_name"
                      value={formData.smtp_from_name}
                      onChange={handleChange}
                      placeholder="Nombre del coach"
                    />
                  </div>
                </>
              )}
            </div>
          )}
          
          {usuario && (
            <>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    name="activo"
                    checked={formData.activo === 1}
                    onChange={handleChange}
                  />
                  {' '}Usuario activo
                </label>
              </div>
              
              <div className="form-group">
                <label>Logo del Usuario</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  disabled={uploadingLogo}
                />
                {logoPreview && (
                  <div style={{ marginTop: '10px' }}>
                    <img 
                      src={logoPreview.startsWith('data:') ? logoPreview : `/${logoPreview}`}
                      alt="Preview del logo"
                      style={{ 
                        maxWidth: '200px', 
                        maxHeight: '100px', 
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        padding: '5px'
                      }}
                    />
                  </div>
                )}
                <small style={{ display: 'block', color: '#666', marginTop: '5px' }}>
                  Formatos permitidos: JPEG, PNG, GIF, WebP. Tamaño máximo: 2MB
                </small>
              </div>
              
              <div className="form-group">
                <label>Favicon del Usuario</label>
                <input
                  type="file"
                  accept=".ico,.png,.svg,.gif,.jpeg,.jpg"
                  onChange={handleFaviconChange}
                  disabled={uploadingFavicon}
                />
                {faviconPreview && (
                  <div style={{ marginTop: '10px' }}>
                    <img 
                      src={faviconPreview.startsWith('data:') ? faviconPreview : `/${faviconPreview}`}
                      alt="Preview del favicon"
                      style={{ 
                        width: '32px', 
                        height: '32px', 
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        padding: '5px',
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                )}
                <small style={{ display: 'block', color: '#666', marginTop: '5px' }}>
                  Formatos permitidos: ICO, PNG, SVG, GIF, JPEG. Tamaño máximo: 1MB
                </small>
              </div>
            </>
          )}

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={uploadingLogo || uploadingFavicon}>
              {(uploadingLogo || uploadingFavicon) ? 'Subiendo...' : (usuario ? 'Actualizar' : 'Crear')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UsuarioForm;

